import { Component } from '../lib/ComponentBase';
import { simpleComponent } from '../lib/ComponentHelpers';
import type { IntentionProvider, ProposalProvider, AttackPositionProvider, DisplayProvider, DisplayInfo, PathfindingNode, WorldState } from '../types/Components';
import type { Entity } from '../types/Entity';
import type { Intention } from '../types/AI';
import type Action from '../lib/Action';
import type { Proposal } from '../lib/Proposal';
import type HexCoords from '../grid/HexCoords';
import getLogger from '../lib/logger';
import { ActionType } from '../lib/Action';
import { ProposalType } from '../lib/Proposal';
import HexPosition from './HexPosition';
import Faction from './Faction';

const logger = getLogger('Components');

/**
 * An ability that lets an actor attack a stationary target in the hex directly in front.
 */
class ForwardBite extends Component implements IntentionProvider, ProposalProvider, AttackPositionProvider, DisplayProvider {
  damage: number;
  priority: number;

  constructor(data: { damage?: number, priority?: number } = {}) {
    super();
    this.damage = data.damage ?? 10;
    this.priority = data.priority ?? 20;
  }

  getIntention(entity: Entity, worldState: WorldState): Intention | null {
    const { occupiedCoords } = worldState;
    const selfPosition = entity.getComponent(HexPosition);
    const selfFaction = entity.getComponent(Faction);

    if (!selfPosition || !selfFaction) return null;

    const targetHex = selfPosition.coords.neighbor(selfPosition.bearing);
    const occupant = occupiedCoords.get(targetHex.key);

    if (occupant && occupant.id !== entity.id) {
      const occupantFaction = occupant.getComponent(Faction);
      if (occupantFaction && occupantFaction.name !== selfFaction.name) {
        // Found an enemy in front. Propose a medium-priority IDLE.
        return {
          priority: 25, // Higher than PathfindingAI (10), lower than OmniBite (50)
          type: ActionType.IDLE,
          details: { coords: targetHex, target: occupant }, // TODO: why do we have to pass target coords and entity? shouldn't the "replacement" effects determine the target? But it doesn't work if we remove this
          sourceComponentName: 'ForwardBite',
        };
      }
    }
    return null;
  }

  getProposedOutcome(selfAction: Action): Proposal | null {
    // This ability only triggers for intentions generated by ForwardBite itself.
    if (selfAction.sourceComponentName !== 'ForwardBite') {
      return null;
    }

    const selfPosition = selfAction.entity.getComponent(HexPosition);
    if (!selfPosition) return null;

    // Check if the target is in the hex directly in front.
    const forwardHex = selfPosition.coords.neighbor(selfPosition.bearing);
    if (!selfAction.details.coords || !selfAction.details.coords.is(forwardHex)) {
      return null;
    }

    const target = selfAction.details.target;
    if (!target) {
      logger.warn('ForwardBite proposed outcome without a target in details.');
      return null;
    }

    const attackEffect = {
      type: 'ATTACK' as const,
      attackHex: selfAction.details.coords,
      attackTiming: 'BOTH' as const,
      damage: this.damage,
      onMiss: [{
        type: 'MISS' as const,
        target,
        animationPlan: [[{
          type: 'ATTACK_JAB' as const,
          entityId: selfAction.entity.id,
          targetCoords: selfAction.details.coords,
        }]]
      }],
      animationPlan: [[{
        type: 'ATTACK_JAB' as const,
        entityId: selfAction.entity.id,
        targetCoords: selfAction.details.coords,
      }]],
    };

    return {
      type: ProposalType.REPLACEMENT,
      priority: this.priority,
      effects: [{ type: 'IDLE' as const }, attackEffect],
    };
  }

  getAttackPositions(entity: Entity, worldState: WorldState): PathfindingNode[] {
    const playerPos = worldState.player?.getComponent(HexPosition);
    if (!playerPos) return [];

    // the attack positions are any neighboring hex, while facing the player
    const positions: PathfindingNode[] = [];
    playerPos.coords.allNeighbors().forEach((hex: HexCoords) => {
      const bearing = hex.isNeighbor(playerPos.coords);
      if (bearing !== false) {
        positions.push({ coords: hex, bearing });
      }
    });
    return positions;
  }

  getDisplayInfo(): DisplayInfo {
    return { icon: 'game-icons:fangs', label: 'Forward Bite', description: 'Bites the hex directly ahead.' };
  }

  getAttackPattern(entity: Entity): HexCoords[] {
    const selfPos = entity.getComponent(HexPosition);
    if (!selfPos) return [];

    // ForwardBite attacks the hex directly in front
    return [selfPos.coords.neighbor(selfPos.bearing)];
  }
}

export default simpleComponent(ForwardBite);
